import axios from 'axios';

import authentication from 'services/authentication';

let clientId, url;

/**
 * Configure Kmi api
 *
 * @param {Object} config - The configuration object
 * @param {String} config.url - The root url for Kmi api
 */
const configure = (config) => {
  ({clientId, url} = config);
};

const formatOrgId = (orgId) => orgId.replace('@', '-');

/**
 * Get headers for Kmi API calls
 *
 * @returns {Object} The header map
 */
const getHeaders = () => {
  const tokenObject = authentication.getAccessToken();
  if (!tokenObject?.token) {
    throw new Error('Unable to get token for KMI API call');
  }
  return {
    Authorization: `Bearer ${tokenObject.token}`,
    'X-Api-Key': clientId,
  };
};

/**
 * @description Fetches current org encryption status
 *
 * @param {String} orgId - The associated org Id
 * @returns {Promise<Object>} Axios response
 */
const getOrgEncryptionStatus = ({orgId}) => {
  const formattedOrgId = formatOrgId(orgId);

  return axios.get(`${url}/organizations/${formattedOrgId}/encryption`, {
    headers: getHeaders(),
  });
};

/**
 * @description Fetches encrypted directories for an organization
 *
 * @param {String} orgId - The associated org Id
 * @returns {Promise<Object>} Axios response
 */
const getEncryptedDirectories = ({orgId}) => {
  const formattedOrgId = formatOrgId(orgId);

  return axios.get(`${url}/organizations/${formattedOrgId}/encrypted-directories`, {
    headers: {Accept: 'application/json;strict=true', ...getHeaders()},
  });
};

/**
 * @description Fetches encrypted domains for an organization
 *
 * @param {String} orgId - The associated org Id
 * @param {String} domainId - The associated domain Id
 * @returns {Promise<Object>} Axios response
 */
const getEncryptedDomains = ({orgId, domainId}) => {
  const formattedOrgId = formatOrgId(orgId);
  return axios.get(`${url}/enterprises/${formattedOrgId}/encrypted-domains/${domainId}`, {
    headers: {Accept: 'application/json;strict=true', ...getHeaders()},
  });
};

/**
 * @description Revokes encryption for an organization
 *
 * @param {String} orgId - The associated org Id
 * @returns {Promise<Object>} Axios response
 */
const revokeOrgEncryption = ({orgId}) => {
  const formattedOrgId = formatOrgId(orgId);
  return axios.put(
    `${url}/organizations/${formattedOrgId}/encryption`,
    {orgId},
    {
      headers: getHeaders(),
    }
  );
};

/**
 * @description Enables encryption for an organization
 *
 * @param {String} orgId - The associated org Id
 * @returns {Promise<Object>} Axios response
 */
const enableOrgEncryption = ({orgId}) => {
  const formattedOrgId = formatOrgId(orgId);
  return axios.post(
    `${url}/organizations/${formattedOrgId}/encryption`,
    {orgId},
    {
      headers: getHeaders(),
    }
  );
};

const kmi = {
  configure,
  enableOrgEncryption,
  getEncryptedDirectories,
  getEncryptedDomains,
  getOrgEncryptionStatus,
  revokeOrgEncryption,
};

export default kmi;
